import { useEffect, useState, useCallback, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { loc } from "@/lib/localize";

function HeroSlide({ slide, isActive }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive) v.play().catch(() => {});
    else v.pause();
  }, [isActive]);

  return (
    <div className="embla__slide relative h-[88vh] min-h-[640px]">
      {slide.video ? (
        <>
          <video
            ref={videoRef}
            src={slide.video}
            poster={slide.image}
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* image fallback while video loads */}
          <img
            src={slide.image}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover -z-10"
          />
        </>
      ) : (
        <img
          src={slide.image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 hero-overlay" />
    </div>
  );
}

export default function HeroSlider() {
  const { t, lang } = useLang();
  const { content } = useContent();
  const navigate = useNavigate();
  const slides = content?.hero_slides || [];
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, duration: 30 });
  const [idx, setIdx] = useState(0);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setIdx(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    onSelect();
    const id = setInterval(() => embla.scrollNext(), 7500);
    return () => clearInterval(id);
  }, [embla, onSelect]);

  if (!slides.length) {
    return (
      <section className="relative h-[80vh] bg-[var(--surface)] grid place-items-center">
        <div className="shimmer absolute inset-0" />
      </section>
    );
  }

  const activeSlide = slides[idx] || slides[0];
  const title = loc(activeSlide.title, lang);
  const subtitle = loc(activeSlide.subtitle, lang);
  const titleParts = title.split(" ");
  const head = titleParts.slice(0, Math.max(1, titleParts.length - 2)).join(" ");
  const tail = titleParts.slice(-2).join(" ");

  return (
    <section className="relative" data-testid="hero-section">
      <div className="embla relative" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((s, i) => (
            <HeroSlide key={s.id || s.image} slide={s} isActive={i === idx} />
          ))}
        </div>
      </div>

      <div className="absolute inset-0 flex items-center pointer-events-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl pointer-events-auto">
            <div className="flex items-center gap-2 mb-6 fade-in">
              <Sparkles size={14} className="text-[var(--gold)]" />
              <span className="overline">{t("hero.tag")}</span>
            </div>
            <h1
              key={lang + idx + "title"}
              className="font-display text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.05] mb-6 fade-up"
              data-testid="hero-title"
            >
              {head && <span className="block text-white">{head}</span>}
              <span className="block gold-text">{tail}</span>
            </h1>
            <p
              key={lang + idx + "sub"}
              className="text-lg sm:text-xl text-white/80 mb-9 max-w-2xl fade-up leading-relaxed"
              style={{ animationDelay: "0.1s" }}
              data-testid="hero-subtitle"
            >
              {subtitle}
            </p>
            <div className="flex flex-wrap gap-4 fade-up" style={{ animationDelay: "0.2s" }}>
              <button
                onClick={() => navigate("/contact")}
                className="btn-gold"
                data-testid="hero-cta-primary"
              >
                {t("hero.ctaPrimary")} <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate("/services")}
                className="btn-outline-gold"
                data-testid="hero-cta-secondary"
              >
                {t("hero.ctaSecondary")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {slides.map((s, i) => (
          <button
            key={s.id || s.image}
            onClick={() => embla && embla.scrollTo(i)}
            data-testid={`hero-dot-${i}`}
            className={`h-1 rounded-full transition-all ${
              i === idx ? "w-10 bg-[var(--gold)]" : "w-5 bg-white/30 hover:bg-white/50"
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Arrows */}
      <button
        onClick={() => embla && embla.scrollPrev()}
        className="hidden md:grid absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 place-items-center rounded-full glass border-white/10 text-white hover:text-[var(--gold)] z-10"
        aria-label="Prev"
        data-testid="hero-prev"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={() => embla && embla.scrollNext()}
        className="hidden md:grid absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 place-items-center rounded-full glass border-white/10 text-white hover:text-[var(--gold)] z-10"
        aria-label="Next"
        data-testid="hero-next"
      >
        <ChevronRight size={22} />
      </button>
    </section>
  );
}
